import React, { useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import EmptyPlaceholderCst from "@components/EmptyPlaceholderCst";
import ItemCardCst from "@components/ItemCardCst";
import LanguageSelectorLinks from "@components/InputCst/LanguageSelector/Links";
import { WizardStepErrors } from "@components/WizardCst/types";
import { UsefulLink } from "../../../types";
import { useLoTranslationContext } from "../../../../hooks/useLoTranslation";
import UsefulLinkModalCst from "./UsefulLinkModalCst";
import { useWizardReadOnly } from "@components/WizardCst";

interface UsefulLinksFieldProps {
  values: UsefulLink[];
  onChange: (next: UsefulLink[]) => void;

  errors?: WizardStepErrors;
}

const UsefulLinksField: React.FC<UsefulLinksFieldProps> = ({
  values,
  onChange,
}) => {
  const { t } = useTranslation("wizard-activity");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();
  const trans = useLoTranslationContext();
  const canTranslate = !!trans?.languages?.secondary_languages?.length;

  const [modalOpen, setModalOpen] = useState(false);
  const readOnly = useWizardReadOnly();

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  const handleAdd = (link: UsefulLink) => {
    onChange([...values, link]);
  };

  const handleRemove = (idx: number) => {
    const next = values.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 4, mb: 1 }}>
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 700,
            color: theme.palette.text.primary,
          }}
        >
          {t("fields.useful-links")}
        </Typography>
        {!readOnly && (
          <Box
            onClick={handleOpenModal}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              cursor: "pointer",
              color: theme.palette.primary.main,
              fontSize: "13px",
              fontWeight: 600,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {t("common.add")}
            <Plus size={14} />
          </Box>
        )}
      </Box>

      {values.length === 0 ? (
        <EmptyPlaceholderCst label={t("common.empty-useful-links")} />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {values.map((link, idx) => (
            <ItemCardCst
              key={idx}
              title={link.url_label}
              segments={[link.url, link.description]}
              actions={
                canTranslate &&
                link.id != null &&
                trans && (
                  <LanguageSelectorLinks
                    languages={trans.languages}
                    col={{ accessorKey: "useful_links" }}
                    id={corporateId}
                    idObject={link.id}
                  />
                )
              }
              onDelete={readOnly ? undefined : () => handleRemove(idx)}
            />
          ))}
        </Box>
      )}

      <UsefulLinkModalCst
        open={modalOpen}
        onClose={handleCloseModal}
        onConfirm={handleAdd}
      />
    </Box>
  );
};

export default UsefulLinksField;
